import type { SecondaryDimensionValues } from '../../../PageConfigProvider/types/dimension';
import type { CompositionMetricValues } from '../../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  type BreakdownTimeseriesDataSet,
  type DataProviderSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../../types';
import {
  type CompositionV3AnyGraphqlResponse,
  type CompositionV3FullMetrics,
  isCompositionV3FullMetrics,
} from './composition-v3-graphql-response';
import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from './is-empty-composition-bucket';

type CompositionV3Payload = CompositionV3AnyGraphqlResponse;

type SegmentTimeseriesAcc = {
  dim: string;
  pointsByTimeId: Record<number, TimeseriesDataPoint>;
};

const uppercaseFirstCharacter = (value: string): string =>
  value.length > 0
    ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
    : value;

const breakdownLabel = (
  rowId: number | string | null | undefined,
  shortName: string
): string =>
  isOtherCompositionBucket(rowId)
    ? OTHER_BUCKET_LABEL
    : uppercaseFirstCharacter(shortName);

const fullMetrics = (
  entity: NonNullable<CompositionV3AnyGraphqlResponse['entities']>[number]
): CompositionV3FullMetrics | undefined =>
  isCompositionV3FullMetrics(entity.metrics) ? entity.metrics : undefined;

const getDateRefForMetricPoint = (
  dateRefs: string[],
  pointId: number
): string => dateRefs[pointId] ?? '';

function sumNullableNumbers(
  a: number | null | undefined,
  b: number | null | undefined
): number | null {
  const aValue = typeof a === 'number' ? a : null;
  const bValue = typeof b === 'number' ? b : null;
  if (aValue === null && bValue === null) return null;
  return (aValue ?? 0) + (bValue ?? 0);
}

function mergeBreakdownPointsByDim(
  points: SingleDataPoint[]
): SingleDataPoint[] {
  const pointsByDim = new Map<string, SingleDataPoint>();

  points.forEach((point) => {
    const existing = pointsByDim.get(point.dim);
    if (existing === undefined) {
      pointsByDim.set(point.dim, point);
      return;
    }

    pointsByDim.set(point.dim, {
      ...existing,
      value: sumNullableNumbers(existing.value, point.value),
      share: sumNullableNumbers(existing.share, point.share),
    });
  });

  return Array.from(pointsByDim.values());
}

// Order breakdown buckets largest-first with "Other" pinned last. The single
// (top-level) composition path already sorts this way; the breakdown path used
// to leave buckets in raw API / Map-insertion order. That mismatch surfaced as
// wrong colour attribution in the nested treemap — colours are assigned by the
// input order of the buckets, but the treemap lays them out largest-first, so
// an unsorted breakdown (notably skills, where `mergeBreakdownPointsByDim`
// re-orders duplicate names) painted cells with the wrong colours.
function sortBreakdownByValueOtherLast(
  points: SingleDataPoint[]
): SingleDataPoint[] {
  return [...points].sort((a, b) => {
    const aOther = a.dim === OTHER_BUCKET_LABEL;
    const bOther = b.dim === OTHER_BUCKET_LABEL;
    if (aOther !== bOther) return aOther ? 1 : -1;
    return (b.value ?? 0) - (a.value ?? 0);
  });
}

const segmentsToSort: SecondaryDimensionValues[] = [
  'company',
  'geography',
  'rics',
  'role',
  'skills',
  'competitors',
];

export const convertCompositionDataV3 = ({
  metric,
  segment,
  data,
  isTimeseries,
  exchangeRate = 1,
  monthRefs,
}: {
  metric: CompositionMetricValues;
  segment?: SecondaryDimensionValues;
  data: CompositionV3Payload;
  isTimeseries?: boolean;
  exchangeRate?: number;
  monthRefs: string[];
}): DataProviderSet => {
  const dateRefs = monthRefs;

  if (segment === 'timeseries') {
    const compositionData: TimeseriesDataSet['data'] = data?.entities?.map(
      (entity) => {
        const dim = uppercaseFirstCharacter(entity.short_name);
        const metricData =
          metric === 'headcount'
            ? entity.metrics.headcount
            : (fullMetrics(entity)?.[metric] ?? []);

        const timeseries: TimeseriesDataPoint[] = metricData.flatMap(
          (metricDataPoint): TimeseriesDataPoint[] => {
            const date = getDateRefForMetricPoint(dateRefs, metricDataPoint.id);
            if (date === '') return [];

            const rawValue = metricDataPoint.val;
            const value =
              metric === 'salary' ? rawValue * exchangeRate : rawValue;

            return [{ date, value }];
          }
        );

        return {
          dim,
          timeseries,
        };
      }
    );

    return { type: DataType.TIMESERIES, data: compositionData };
  }

  if (segment && segment !== 'company' && segment !== 'competitors') {
    const hasTimeseriesData = data?.entities?.some((entity) => {
      const metricData = fullMetrics(entity)?.[segment];
      return metricData?.some((b) => b.headcount.length > 0);
    });

    if (hasTimeseriesData && isTimeseries) {
      const segmentTimeseriesMap: Record<string, SegmentTimeseriesAcc> = {};

      data?.entities?.forEach((entity) => {
        const metricData = (fullMetrics(entity)?.[segment] ?? []).filter(
          (brk) => !isEmptyCompositionBucket(brk?.id)
        );

        metricData.forEach((brk) => {
          const segmentName = breakdownLabel(brk.id, brk.short_name);
          if (!segmentName) return;

          if (!segmentTimeseriesMap[segmentName]) {
            segmentTimeseriesMap[segmentName] = {
              dim: segmentName,
              pointsByTimeId: {},
            };
          }

          const { pointsByTimeId } = segmentTimeseriesMap[segmentName];

          for (const timePoint of brk.headcount) {
            if (!timePoint) continue;

            const timePointId = timePoint.id;
            const date = dateRefs[timePointId];
            if (!date) continue;

            const existingPoint = pointsByTimeId[timePointId];
            if (existingPoint) {
              const prevVal =
                typeof existingPoint.value === 'number'
                  ? existingPoint.value
                  : 0;
              existingPoint.value = prevVal + timePoint.count;
            } else {
              pointsByTimeId[timePointId] = {
                date,
                value: timePoint.count,
              };
            }
          }
        });
      });

      const expectedSegments = Object.keys(segmentTimeseriesMap);

      const allTimeIds = (() => {
        const ids = new Set<number>();
        Object.values(segmentTimeseriesMap).forEach((segmentSeries) => {
          Object.keys(segmentSeries.pointsByTimeId).forEach((k) => {
            ids.add(Number(k));
          });
        });
        return [...ids].sort((a, b) => a - b);
      })();

      const totalCountByTimeId: Record<number, number> = {};
      allTimeIds.forEach((timeId) => {
        totalCountByTimeId[timeId] = Object.values(segmentTimeseriesMap).reduce(
          (acc, segmentSeries) => {
            const p = segmentSeries.pointsByTimeId[timeId];
            return acc + (typeof p?.value === 'number' ? p.value : 0);
          },
          0
        );
      });

      const compositionData: BreakdownTimeseriesDataSet['data'] =
        expectedSegments.map(
          (segmentName): BreakdownData<TimeseriesDataPoint> => {
            const item = segmentTimeseriesMap[segmentName] ?? {
              dim: segmentName,
              pointsByTimeId: {},
            };
            return {
              dim: item.dim,
              breakdown: allTimeIds.map((timeId) => {
                const point = item.pointsByTimeId[timeId];
                const total = totalCountByTimeId[timeId] ?? 0;
                const value =
                  point !== undefined && typeof point.value === 'number'
                    ? point.value
                    : null;
                const date = dateRefs[timeId] ?? point?.date ?? '';

                const share = (() => {
                  if (value === null) return null;
                  if (total <= 0) return null;
                  return value / total;
                })();

                return {
                  date,
                  value,
                  share,
                };
              }),
            };
          }
        );

      return { type: DataType.BREAKDOWN_TIMESERIES, data: compositionData };
    }

    const compositionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
      (entity) => {
        const metricData = fullMetrics(entity)?.[segment];
        const breakdown = sortBreakdownByValueOtherLast(
          mergeBreakdownPointsByDim(
            metricData
              ?.filter(
                (metricDataPoint) =>
                  !isEmptyCompositionBucket(metricDataPoint?.id)
              )
              ?.map((metricDataPoint): SingleDataPoint => {
                const hc = metricDataPoint.headcount;
                const last = hc[hc.length - 1];
                const breakdownEntityId = metricDataPoint.id;

                return {
                  dim: breakdownLabel(
                    breakdownEntityId,
                    metricDataPoint.short_name
                  ),
                  value: last ? last.count : null,
                  share: last ? last.share : null,
                  drilldownId: String(breakdownEntityId),
                };
              }) ?? []
          )
        );

        return {
          dim: uppercaseFirstCharacter(entity.short_name),
          breakdown,
        };
      }
    );

    return { type: DataType.BREAKDOWN_SINGLE, data: compositionData };
  }

  const compositionData: SingleDataSet['data'] = data?.entities?.map(
    (entity): SingleDataPoint => {
      const dim = uppercaseFirstCharacter(entity.short_name);
      const metricData = fullMetrics(entity)?.[metric];
      const lastMetricData = metricData?.[metricData.length - 1];
      const rawValue = lastMetricData ? lastMetricData.val : null;
      const value =
        typeof rawValue === 'number' && metric === 'salary'
          ? rawValue * exchangeRate
          : rawValue;

      return {
        dim,
        value,
      };
    }
  );

  const sortedCompositionData = (() => {
    if (segment && segmentsToSort.includes(segment)) {
      return compositionData.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }
    return compositionData;
  })();

  return { type: DataType.SINGLE, data: sortedCompositionData };
};

export const convertCompositionTopNHeadcountDataV3 = ({
  segment,
  isGrouped,
  data,
}: {
  segment: Exclude<
    SecondaryDimensionValues,
    'company' | 'timeseries' | 'competitors'
  >;
  isGrouped?: boolean;
  data: CompositionV3Payload;
}): SingleDataSet | BreakdownSingleDataSet => {
  if (isGrouped) {
    const compositionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
      (entity): BreakdownData<SingleDataPoint> => ({
        dim: uppercaseFirstCharacter(entity.short_name),
        // Sort like the ungrouped path so the nested treemap colours buckets by
        // value order regardless of which converter produced the data.
        breakdown: sortBreakdownByValueOtherLast(
          mergeBreakdownPointsByDim(
            fullMetrics(entity)
              ?.[segment]?.filter((row) => !isEmptyCompositionBucket(row?.id))
              ?.map((row) => {
                const hc = row.headcount;
                const last = hc[hc.length - 1];
                const breakdownEntityId = row.id;
                return {
                  dim: breakdownLabel(breakdownEntityId, row.short_name),
                  value: last ? last.count : null,
                  share: last ? last.share : null,
                  drilldownId: String(breakdownEntityId),
                };
              }) ?? []
          )
        ),
      })
    );

    return { type: DataType.BREAKDOWN_SINGLE, data: compositionData };
  }

  const compositionDataObject = data?.entities?.reduce<
    Record<string, SingleDataPoint>
  >((acc, entity) => {
    const breakdownData = fullMetrics(entity)?.[segment];

    breakdownData
      ?.filter((row) => !isEmptyCompositionBucket(row?.id))
      ?.forEach((row) => {
        const dim = breakdownLabel(row.id, row.short_name);
        const hc = row.headcount;
        const last = hc[hc.length - 1];
        const value = last ? last.count : null;

        if (!acc[dim]) acc[dim] = { dim, value: value ?? 0 };
        else acc[dim].value = (acc[dim].value ?? 0) + (value ?? 0);
      });

    return acc;
  }, {});

  const compositionData: SingleDataSet['data'] = Object.values(
    compositionDataObject ?? {}
  );
  const sortedCompositionData = (() => {
    if (segmentsToSort.includes(segment)) {
      return compositionData.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }
    return compositionData;
  })();

  return { type: DataType.SINGLE, data: sortedCompositionData };
};
